if (!customElements.get('muralls-customer-gallery')) {
  customElements.define('muralls-customer-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.links = [...this.querySelectorAll('[data-photo-open]')];
      this.track = this.querySelector('[data-photo-track]');
      this.dialog = this.querySelector('dialog');
      this.image = this.querySelector('[data-viewer-image]');
      this.loading = this.querySelector('[data-viewer-loading]');
      this.thumbTrack = this.querySelector('[data-viewer-thumbs]');
      this.currentIndex = 0;
      this.thumbTrack.replaceChildren();

      this.links.forEach((link, index) => {
        link.addEventListener('click', (event) => {
          event.preventDefault();
          this.opener = link;
          this.showPhoto(index);
          this.hadScrollLock = document.documentElement.classList.contains('pcg-open');
          this.dialog.showModal();
          document.documentElement.classList.add('pcg-open');
        }, options);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pcg-viewer__thumb';
        button.setAttribute('aria-label', `View photo ${index + 1} by ${link.dataset.name}`);
        button.setAttribute('aria-pressed', 'false');
        const thumb = document.createElement('img');
        const url = new URL(link.href);
        url.searchParams.set('width', '120');
        thumb.src = url.href;
        thumb.alt = '';
        thumb.width = 44;
        thumb.height = 44;
        thumb.loading = 'lazy';
        button.append(thumb);
        button.addEventListener('click', () => this.showPhoto(index), options);
        this.thumbTrack.append(button);
      });

      this.querySelector('[data-viewer-close]').addEventListener('click', () => this.dialog.close(), options);
      this.querySelector('[data-viewer-prev]').addEventListener('click', () => this.showPhoto(this.currentIndex - 1), options);
      this.querySelector('[data-viewer-next]').addEventListener('click', () => this.showPhoto(this.currentIndex + 1), options);
      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'Tab') {
          const buttons = [...this.dialog.querySelectorAll('button:not(:disabled)')]
            .filter((button) => button.getClientRects().length);
          const first = buttons[0];
          const last = buttons[buttons.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.showPhoto(this.currentIndex + (event.key === 'ArrowRight' ? 1 : -1));
        }
      }, options);
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog || event.target.matches('[data-viewer-stage], [data-viewer-image-area]')) {
          this.dialog.close();
        }
      }, options);
      this.dialog.addEventListener('close', () => this.restorePage(), options);
      this.image.addEventListener('load', () => {
        this.loading.hidden = true;
        this.image.hidden = false;
        this.image.removeAttribute('aria-busy');
      }, options);
      this.image.addEventListener('error', () => {
        this.image.hidden = true;
        this.image.removeAttribute('aria-busy');
        this.loading.textContent = 'This photo could not load. Please try again.';
        this.loading.hidden = false;
      }, options);

      const imageArea = this.querySelector('[data-viewer-image-area]');
      imageArea.addEventListener('touchstart', (event) => {
        this.touch = event.touches.length === 1
          ? { x: event.touches[0].clientX, y: event.touches[0].clientY, time: Date.now() }
          : null;
      }, { ...options, passive: true });
      imageArea.addEventListener('touchend', (event) => {
        if (!this.touch) return;
        const dx = event.changedTouches[0].clientX - this.touch.x;
        const dy = event.changedTouches[0].clientY - this.touch.y;
        if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.4 && Date.now() - this.touch.time < 700) {
          this.showPhoto(this.currentIndex + (dx < 0 ? 1 : -1));
        }
        this.touch = null;
      }, { ...options, passive: true });
      imageArea.addEventListener('touchcancel', () => { this.touch = null; }, options);

      this.querySelector('[data-track-prev]').addEventListener('click', () => this.scrollPhotos(-1), options);
      this.querySelector('[data-track-next]').addEventListener('click', () => this.scrollPhotos(1), options);
      this.track.addEventListener('scroll', () => this.updateNavigation(), { ...options, passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateNavigation());
      this.resizeObserver.observe(this.track);
      this.updateNavigation();
    }

    showPhoto(index) {
      this.currentIndex = (index + this.links.length) % this.links.length;
      const link = this.links[this.currentIndex];
      this.loading.textContent = 'Loading photo…';
      this.loading.hidden = false;
      this.image.hidden = true;
      this.image.setAttribute('aria-busy', 'true');
      this.image.alt = link.dataset.alt;
      this.image.src = link.href;
      this.querySelector('[data-viewer-name]').textContent = link.dataset.name;
      this.querySelector('[data-viewer-room]').textContent = link.dataset.room;
      this.querySelector('[data-viewer-count]').textContent = `${this.currentIndex + 1} / ${this.links.length}`;
      [...this.thumbTrack.children].forEach((button, i) => button.setAttribute('aria-pressed', String(i === this.currentIndex)));
      const thumb = this.thumbTrack.children[this.currentIndex];
      this.thumbTrack.scrollTo({ left: thumb.offsetLeft - this.thumbTrack.offsetLeft - (this.thumbTrack.clientWidth - thumb.offsetWidth) / 2 });
      if (this.image.complete && this.image.naturalWidth) {
        this.loading.hidden = true;
        this.image.hidden = false;
        this.image.removeAttribute('aria-busy');
      }
    }

    scrollPhotos(direction) {
      const firstCard = this.track.firstElementChild;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const step = firstCard.getBoundingClientRect().width + gap;
      this.track.scrollBy({ left: direction * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    updateNavigation() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelector('[data-photo-browse]').hidden = max < 2;
      this.querySelector('[data-track-prev]').disabled = this.track.scrollLeft <= 2;
      this.querySelector('[data-track-next]').disabled = this.track.scrollLeft >= max - 2;
    }

    restorePage() {
      if (!this.hadScrollLock) document.documentElement.classList.remove('pcg-open');
      if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
    }

    disconnectedCallback() {
      if (this.dialog?.open) {
        this.dialog.close();
        this.restorePage();
      }
      this.events?.abort();
      this.events = null;
      this.resizeObserver?.disconnect();
    }
  });
}
